import { useRef } from 'react';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import {
  getTickets,
  getTicket,
  getCloseTicketReasons,
  getLocation,
  getAnnouncements,
  getSupportCategories,
  getSupportCategoryItem,
  getFormLayout,
  getStaffLocations,
  searchAllSupport,
  searchTickets,
  getAllLocations,
  getRelatedMinorCases,
  getKBArticle,
  getRecentSearches,
  getHelpfulRecentKBArticles,
  getAppointmentDetails,
  getKBFeedbackReasons,
  getAssets,
  getRecentlyViewedAssets,
  getAssetResources,
  getSupportCategoriesAsset,
  getAirshipUserProperties,
  getCasePrompts,
  getTicketLocation,
  getAssetWarranties,
  getUserEventInfo,
  getTroubleshootingFlows,
  getTroubleshootingFlow,
  getTroubleshootingFlowNextStep,
  getAppointments,
  getMemberships,
  getFacilityCategories,
  getServiceHistory,
  getServiceEvent,
  submitKBFeedback,
  getSupportAppConfig,
  getRecentlyViewedTickets,
} from './requests';
import {
  type AppointmentQueryVariables,
  type AppointmentsQueryVariables,
  type AssetResourcesQueryVariables,
  type AssetServiceEventQueryVariables,
  type AssetServiceHistoryQueryVariables,
  type AssetsQueryVariables,
  type AssetsRecentQueryVariables,
  type AssetWarrantiesQueryVariables,
  type CasePromptsQueryVariables,
  CasePromptType,
  type CasesQueryVariables,
  type CasesRecentQueryVariables,
  type FlowInfosQueryVariables,
  type FlowQueryVariables,
  type KbArticleQueryVariables,
  type NextFlowStepQueryVariables,
  type OpenCasesQueryVariables,
  type ProgramMembershipsQueryVariables,
  type RelatedCasesQueryVariables,
  type RestaurantQueryVariables,
  type SearchAllSupportQueryVariables,
  type SearchCasesQueryVariables,
  type SearchesRecentQueryVariables,
  type SubmitKBFeedbackMutationVariables,
  type SupportCategoriesAssetQueryVariables,
  type TicketQueryVariables,
  type UserEventInfoQueryVariables,
} from '../../../API';
import {
  shouldRetryTroubleshootingQuery,
  troubleshootingRetryDelay,
} from '../retryability';

export const useGetTickets = (
  locationNum: CasesQueryVariables['locationNum'],
  locationAssigned: boolean,
  user: OpenCasesQueryVariables['contactGuid'],
  enabled: boolean,
  filter: string,
  page: CasesQueryVariables['page']
) => {
  return useQuery({
    queryKey: ['all-tickets', locationNum, filter, page],
    queryFn: () => getTickets(locationNum, page ?? 1, user, filter),

    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 0,
    enabled: !!filter && !!locationNum && locationAssigned && enabled,
  });
};

export const useSupportAppConfig = () => {
  return useQuery({
    queryKey: ['supportAppConfig'],
    queryFn: getSupportAppConfig,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useGetTicket = (id: TicketQueryVariables['id']) =>
  useQuery({
    queryKey: ['get-ticket', id],
    queryFn: () => getTicket({ id, markRead: true }),

    refetchOnWindowFocus: false,
  });

export const useGetTicketLocation = (id: TicketQueryVariables['id']) =>
  useQuery({
    queryKey: ['get-ticket-location', id],
    queryFn: () => getTicketLocation({ id, markRead: false }),

    refetchOnWindowFocus: false,
  });

export const useGetCloseTicketReasons = (enabled: boolean) =>
  useQuery({
    queryKey: ['close-ticket-reasons'],
    queryFn: () => getCloseTicketReasons(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: enabled,
  });

export const useGetLocation = (
  locationNum: RestaurantQueryVariables['locationNum'],
  enabled: boolean,
  { retryOnMount = true }: { retryOnMount?: boolean } = {}
) =>
  useQuery({
    queryKey: ['get-location', locationNum],
    queryFn: () => getLocation(locationNum),

    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: enabled,
    retryOnMount,
  });

export const useGetHomepageLocationData = (
  location: string,
  locationAssigned: boolean,
  user: string,
  enabled: boolean,
  filter: string
) =>
  //useQueries expects an object with a queries property containing the array of query configurations (fixed in tanstack/react-query upgrade)
  useQueries({
    queries: [
      {
        queryKey: ['get-appointments-home-page', location],
        queryFn: () =>
          getAppointments(
            {
              locationNums: [
                location,
              ] as AppointmentsQueryVariables['locationNums'],
            },
            true
          ),

        refetchOnWindowFocus: false,
        enabled: !!location && location !== '00000',
      },
      {
        queryKey: ['all-tickets', location, filter],
        queryFn: () => getTickets(location, 1, user, filter),

        refetchOnWindowFocus: false,
        enabled: !!filter && !!location && locationAssigned && enabled,
      },
      {
        queryKey: ['get-support-categories'],
        queryFn: () => getSupportCategories(),

        refetchOnWindowFocus: false,
        staleTime: Infinity,
      },
    ],
  });

export const useGetAnnouncements = (enabled?: boolean) =>
  useQuery({
    queryKey: ['get-announcements'],
    queryFn: () => getAnnouncements(),

    refetchOnWindowFocus: false,
    enabled,
  });

export const useGetSupportCategories = () =>
  useQuery({
    queryKey: ['get-support-categories'],
    queryFn: () => getSupportCategories(),

    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

export const useGetSupportCategoryItem = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: ['get-support-category-item', id],
    queryFn: () => getSupportCategoryItem(id),

    refetchOnWindowFocus: false,
    enabled: enabled,
  });

export const useGetFormLayout = (
  location: string,
  id: string,
  defaults?: { id: string; value: string | undefined }[],
  enabled?: boolean
) =>
  useQuery({
    queryKey: ['get-form-layout', location, id, defaults],
    queryFn: () => getFormLayout(location, id, defaults),

    refetchOnWindowFocus: false,
    enabled: enabled ?? false,
  });

export const useGetStaffLocations = (
  userId: string,
  enabled: boolean,
  activeLocation: string,
  isAdmin: boolean
) =>
  useQuery({
    queryKey: ['staff-locations', activeLocation],
    queryFn: () => getStaffLocations(userId, isAdmin),

    refetchOnWindowFocus: false,
    enabled,
  });

export const useGetAllLocations = (isEnabled: boolean, isAdmin: boolean) =>
  useQuery({
    queryKey: ['all-locations'],
    queryFn: () => getAllLocations(isAdmin),

    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });

export const useSearchAllSupport = (
  contains: SearchAllSupportQueryVariables['contains'],
  context?: SearchAllSupportQueryVariables['context'],
  ignoreTrackSearch?: SearchAllSupportQueryVariables['ignoreTrackSearch']
) =>
  useQuery({
    queryKey: ['search-all-support', contains, context, ignoreTrackSearch],
    queryFn: () => searchAllSupport({ contains, context, ignoreTrackSearch }),

    refetchOnWindowFocus: false,
    enabled: !!contains,
  });

export const useGetMinorCases = (
  majorCaseId: RelatedCasesQueryVariables['majorCaseId'],
  enabled = true
) =>
  useQuery({
    queryKey: ['get-related-minor-cases', majorCaseId],
    queryFn: () => getRelatedMinorCases({ majorCaseId }),

    refetchOnWindowFocus: false,
    enabled,
  });

export const useGetKBArticle = (
  id: KbArticleQueryVariables['id'],
  foundViaQueryId: KbArticleQueryVariables['foundViaQueryId'],
  foundViaResultId: KbArticleQueryVariables['foundViaResultId']
) =>
  useQuery({
    queryKey: ['get-kb-article', id, foundViaQueryId, foundViaResultId],
    queryFn: () => getKBArticle({ id, foundViaQueryId, foundViaResultId }),

    refetchOnWindowFocus: false,
    enabled: !!id,
  });

export const useGetRecentSearches = (
  userGuid: SearchesRecentQueryVariables['userGuid']
) =>
  useQuery({
    queryKey: ['get-recent-searches', userGuid],
    queryFn: () => getRecentSearches({ userGuid }),

    refetchOnWindowFocus: false,
    enabled: !!userGuid,
  });

export const useGetHelpfulRecentKBArticles = () =>
  useQuery({
    queryKey: ['get-helpful-recent-articles'],
    queryFn: () => getHelpfulRecentKBArticles(),

    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

export const useGetAppointments = (
  locationNums: AppointmentsQueryVariables['locationNums'],
  enabled: boolean
) =>
  useQuery({
    queryKey: ['get-appointments-management-page', locationNums],
    queryFn: () => getAppointments({ locationNums }),

    refetchOnWindowFocus: false,
    enabled,
  });

export const useGetAppointmentDetails = (id: AppointmentQueryVariables['id']) =>
  useQuery({
    queryKey: ['get-appointment-details', id],
    queryFn: () => getAppointmentDetails({ id }),

    refetchOnWindowFocus: false,
    enabled: !!id,
  });

export const useGetKBFeedbackReasons = (enabled: boolean) =>
  useQuery({
    queryKey: ['kb-feedback-reasons'],
    queryFn: () => getKBFeedbackReasons(),
    staleTime: Infinity,
    enabled: enabled,
    refetchOnWindowFocus: false,
  });

export const useGetAssets = (
  locationNums: AssetsQueryVariables['locationNums'],
  enabled: boolean
) =>
  useQuery({
    queryKey: ['get-assets', locationNums],
    queryFn: () => getAssets({ locationNums }),
    enabled: enabled,
    staleTime: 60000, // 60 seconds
    cacheTime: 60000, // 60 seconds
    refetchOnWindowFocus: true,
  });

export const useGetRecentAssets = (
  guid: AssetsRecentQueryVariables['userGuid']
) =>
  useQuery({
    queryKey: ['get-recent-assets', guid],
    queryFn: () => getRecentlyViewedAssets(guid),

    refetchOnWindowFocus: false,
    enabled: !!guid,
  });

export const useGetAssetResources = (
  locationNum: AssetResourcesQueryVariables['locationNum'],
  enabled: boolean
) =>
  useQuery({
    queryKey: ['get-asset-resources', locationNum],
    queryFn: () => getAssetResources({ locationNum }),

    enabled: enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

export const useGetSupportCategoriesAsset = (
  context: SupportCategoriesAssetQueryVariables['context'],
  enabled: boolean
) =>
  useQuery({
    queryKey: ['get-support-categories-asset', context],
    queryFn: () => getSupportCategoriesAsset({ context }),

    enabled: enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

export const useGetAirshipUserProperties = (guid: string, enabled: boolean) =>
  useQuery({
    queryKey: ['get-airship-user-properties', guid],
    queryFn: () => getAirshipUserProperties(guid),

    refetchOnWindowFocus: false,
    enabled: !!guid && enabled,
  });

const MAX_POLL_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 2000;
const ASYNC_PROMPT_TYPES = [
  CasePromptType.DISPATCH_TECHNICIAN,
  CasePromptType.PM_DISPATCH_TECHNICIAN,
];

export const casePromptsPollInterval = (
  promptTypes: CasePromptsQueryVariables['promptTypes'],
  data: unknown,
  attempts: number
): number | false => {
  const awaited = ASYNC_PROMPT_TYPES.filter(type =>
    promptTypes?.includes(type)
  );
  if (!awaited.length) return false;

  const arrived = (type: CasePromptType) =>
    Array.isArray(data) &&
    data.some(
      prompt =>
        (prompt as { promptType?: CasePromptType } | null)?.promptType === type
    );

  if (awaited.every(arrived) || attempts >= MAX_POLL_ATTEMPTS) return false;
  return POLL_INTERVAL_MS;
};

export const useGetCasePrompts = (
  caseId: CasePromptsQueryVariables['caseId'],
  promptTypes: CasePromptsQueryVariables['promptTypes'],
  enabled: boolean
) => {
  const pollCount = useRef(0);

  return useQuery({
    queryKey: ['get-case-prompts', caseId, promptTypes],
    queryFn: () => getCasePrompts({ caseId, promptTypes }),
    refetchOnWindowFocus: false,
    enabled,
    refetchInterval: data => {
      const interval = casePromptsPollInterval(
        promptTypes,
        data,
        pollCount.current
      );
      if (interval === false) return false;
      pollCount.current += 1;
      return interval;
    },
  });
};

export const useGetAssetWarranties = (
  assetId: AssetWarrantiesQueryVariables['assetId'],
  enabled: boolean
) =>
  useQuery({
    queryKey: ['get-asset-warranties', assetId],
    queryFn: () => getAssetWarranties({ assetId }),

    refetchOnWindowFocus: false,
    enabled: enabled,
  });

export const useGetUserEventInfo = (
  eventType: UserEventInfoQueryVariables['eventType'],
  filter?: UserEventInfoQueryVariables['filter']
) =>
  useQuery({
    queryKey: ['get-user-event-info', eventType, filter],
    queryFn: () => getUserEventInfo({ eventType, filter }),

    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

export const useGetTroubleshootingFlows = (
  locationNum: FlowInfosQueryVariables['locationNum']
) =>
  useQuery({
    queryKey: ['get-troubleshooting-flows', locationNum],
    queryFn: () => getTroubleshootingFlows({ locationNum }),

    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

export const useGetTroubleshootingFlow = (
  flowId: FlowQueryVariables['flowId'],
  assetId: FlowQueryVariables['assetId'],
  locationNum: FlowQueryVariables['locationNum'],
  enabled: boolean
) =>
  useQuery({
    queryKey: ['get-troubleshooting-flow', flowId, assetId, locationNum],
    queryFn: () => getTroubleshootingFlow({ flowId, assetId, locationNum }),

    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: enabled,
    // Retry per the CFA error-handling table (HTTP status + errorType).
    retry: shouldRetryTroubleshootingQuery,
    retryDelay: troubleshootingRetryDelay,
  });

export const useGetTroubleshootingFlowNextStep = (
  flowId: NextFlowStepQueryVariables['flowId'],
  currStepId: NextFlowStepQueryVariables['currStepId'],
  inputs: NextFlowStepQueryVariables['inputs'],
  locationNum: FlowQueryVariables['locationNum'],
  enabled: boolean
) =>
  useQuery({
    queryKey: [
      'get-troubleshooting-flow-next-step',
      flowId,
      currStepId,
      inputs,
      locationNum,
    ],
    queryFn: () =>
      getTroubleshootingFlowNextStep({
        flowId,
        currStepId,
        inputs,
        locationNum,
      }),

    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: enabled,
    // Retry per the CFA error-handling table (HTTP status + errorType).
    retry: shouldRetryTroubleshootingQuery,
    retryDelay: troubleshootingRetryDelay,
  });

export const useGetMemberships = (
  locationNums: ProgramMembershipsQueryVariables['locationNums'],
  userGuid: ProgramMembershipsQueryVariables['userGuid'],
  enabled: boolean
) =>
  useQuery({
    queryKey: ['get-memberships', locationNums, userGuid],
    queryFn: () => getMemberships({ locationNums, userGuid }),

    refetchOnWindowFocus: false,
    enabled: enabled,
  });

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const useGetServiceHistory = (
  locationNum: AssetServiceHistoryQueryVariables['locationNum'],
  assetId: AssetServiceHistoryQueryVariables['assetId']
) =>
  useQuery({
    queryKey: ['get-service-history', locationNum, assetId],
    queryFn: () => getServiceHistory({ locationNum, assetId }),

    refetchOnWindowFocus: false,
    enabled: !!assetId,
    // Stale-while-revalidate: render the cached list immediately, then refetch
    // on view so the user always ends up on the freshest data. cacheTime keeps
    // the previous data in cache so it shows instantly during the background
    // refetch instead of flashing a loading state.
    staleTime: 0,
    cacheTime: ONE_DAY_MS,
  });

export const useGetServiceEvent = (
  eventId: AssetServiceEventQueryVariables['eventId']
) =>
  useQuery({
    queryKey: ['get-service-event', eventId],
    queryFn: () => getServiceEvent({ eventId }),

    refetchOnWindowFocus: false,
    enabled: !!eventId,
    staleTime: 0,
    cacheTime: ONE_DAY_MS,
  });

export const useGetSupportAppConfig = (enabled: boolean) =>
  useQuery({
    queryKey: ['support-app-config'],
    queryFn: () => getSupportAppConfig(),
    staleTime: Infinity,
    enabled: enabled,
    refetchOnWindowFocus: false,
  });

export const useSubmitKBFeedback = () =>
  useMutation({
    mutationFn: (variables: SubmitKBFeedbackMutationVariables) =>
      submitKBFeedback(variables),
  });

export const useSearchTickets = (
  contains: SearchCasesQueryVariables['contains'],
  filters?: SearchCasesQueryVariables['filters']
) =>
  useQuery({
    queryKey: ['search-tickets', contains, filters],
    queryFn: () => searchTickets({ contains, filters }),

    refetchOnWindowFocus: false,
    enabled: !!contains,
  });

export const useGetFacilityCategories = (enabled: boolean) =>
  useQuery({
    queryKey: ['get-facility-categories'],
    queryFn: () => getFacilityCategories(),

    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });

export const useGetRecentlyViewedTickets = (
  locationNums: CasesRecentQueryVariables['locationNums'],
  enabled: boolean
) =>
  useQuery({
    queryKey: ['get-recently-viewed-tickets', locationNums],
    queryFn: () => getRecentlyViewedTickets(locationNums),
    refetchOnWindowFocus: false,
    enabled: enabled && locationNums.length > 0,
  });
